'use client'

import { useEffect, useState } from 'react'
import useSWR from 'swr'
import type { Offer } from '@/utils/offers'
import {
	acceptedBids,
	fetchIncomingOffers,
	isActiveOffer,
	markSeenAcceptedBids,
	readSeenAcceptedBids
} from '@/utils/offer-inbox'

export type OfferNotification =
	| { kind: 'offer_waiting'; id: string; at: number; offer: Offer }
	| { kind: 'bid_accepted'; id: string; at: number; offer: Offer }
	/** A live bid of mine: manageable (update, cancel) but never "unread". */
	| { kind: 'bid_active'; id: string; at: number; offer: Offer }

/**
 * What the offers side of the notifications bell shows for one wallet:
 * offers waiting on the wallet's items and the wallet's own bids a seller
 * accepted, newest first, with per-browser "seen" marks. Waiting offers
 * share their seen list with the You've Got Money dialog; accepted bids
 * share theirs with the Bid Accepted dialog.
 */
export default function useOfferNotifications(address: string | null): {
	items: OfferNotification[]
	waiting: number
	accepted: number
	active: number
	unread: number
	loading: boolean
	error: unknown
	isUnread: (item: OfferNotification) => boolean
	markSeen: () => void
	refresh: () => void
} {
	const [now, setNow] = useState(Date.now)
	const [seenOffers, setSeenOffers] = useState<Set<string>>(new Set())
	const [seenAccepted, setSeenAccepted] = useState<Set<string> | null>(null)
	const offersKey = address ? `ow:seen-offers:v1:${address}` : null
	const { data, error, isLoading, mutate } = useSWR(
		address ? ['offers:incoming', address] : null,
		([, address]) => fetchIncomingOffers(address as string),
		{ refreshInterval: 30_000, revalidateOnFocus: true }
	)
	useEffect(() => {
		if (!address) return
		const read = () => {
			try {
				const v = JSON.parse(localStorage.getItem(offersKey!) || '[]')
				setSeenOffers(new Set(Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []))
			} catch {
				setSeenOffers(new Set())
			}
			if (data) setSeenAccepted(readSeenAcceptedBids(address, acceptedBids(data, address)))
		}
		read()
		window.addEventListener('storage', read)
		window.addEventListener('ow:offers-seen', read)
		window.addEventListener('ow:accepted-bids-seen', read)
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => {
			clearInterval(timer)
			window.removeEventListener('storage', read)
			window.removeEventListener('ow:offers-seen', read)
			window.removeEventListener('ow:accepted-bids-seen', read)
		}
	}, [address, offersKey, data])

	const waiting = address
		? (data?.received || []).filter(
				(o) =>
					o.inscription_id &&
					o.seller_address === address &&
					o.buyer_address !== address &&
					isActiveOffer(o, now)
		  )
		: []
	const accepted = address ? acceptedBids(data, address, now) : []
	const active = address
		? (data?.sent || []).filter(
				(o) =>
					o.state === 'active' &&
					(o.buyer_address === address || o.buyer_payment_address === address) &&
					Date.parse(o.expires_at) > now
		  )
		: []
	const items: OfferNotification[] = [
		...waiting.map((offer) => ({
			kind: 'offer_waiting' as const,
			id: `waiting:${offer.id}`,
			at: new Date(offer.created_at).getTime(),
			offer
		})),
		...accepted.map((offer) => ({
			kind: 'bid_accepted' as const,
			id: `accepted:${offer.id}`,
			at: new Date(offer.updated_at).getTime(),
			offer
		})),
		...active.map((offer) => ({
			kind: 'bid_active' as const,
			id: `active:${offer.id}`,
			at: new Date(offer.created_at).getTime(),
			offer
		}))
	].sort((a, b) => b.at - a.at)
	const isUnread = (item: OfferNotification) =>
		item.kind === 'offer_waiting'
			? !seenOffers.has(item.offer.id)
			: item.kind === 'bid_accepted'
			? !!seenAccepted && !seenAccepted.has(item.offer.id)
			: false
	const unread = items.filter(isUnread).length
	const markSeen = () => {
		if (!address) return
		if (waiting.length) {
			const ids = Array.from(new Set([...seenOffers, ...waiting.map((o) => o.id)])).slice(-1000)
			setSeenOffers(new Set(ids))
			try {
				localStorage.setItem(offersKey!, JSON.stringify(ids))
				window.dispatchEvent(new Event('ow:offers-seen'))
			} catch {}
		}
		if (accepted.length)
			setSeenAccepted(
				markSeenAcceptedBids(address, seenAccepted || new Set(), accepted.map((o) => o.id))
			)
	}
	return {
		items,
		waiting: waiting.length,
		accepted: accepted.length,
		active: active.length,
		unread,
		loading: !!address && isLoading,
		error,
		isUnread,
		markSeen,
		refresh: () => void mutate()
	}
}
